import React, { useState, useEffect, useCallback, useRef } from "react";
import { useSelector } from "react-redux";
import { useIntl } from "react-intl";
import { motion, AnimatePresence } from "framer-motion";
import {
  Box,
  Typography,
  Pagination,
  Tooltip,
  Dialog,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import { Send, List, Kanban, X, FileText, ArrowRight, CheckCircle, AlertCircle } from "lucide-react";
import moment from "moment";
import "moment/locale/fr";
import "moment/locale/nl";
import axios from "axios";
import sh from "bundles/common/utils/sh";
import { getDenialReasons } from "bundles/common/utils/utils";
import { getAppPipeline } from "./applicationPipeline";
import SquarehubLoader from "bundles/common/components/SquarehubLoader";
import trackEvent from "bundles/common/utils/trackEvent";
import CandidateKanbanBoard from "./CandidateKanbanBoard";
import EmptyState from "./EmptyState";

const filterConfig = [
  { key: "all", labelId: "application.all_candidat", fallback: "Toutes" },
  { key: "pending", labelId: "application.pending", fallback: "En attente" },
  { key: "open", labelId: "application.open", fallback: "En cours" },
  { key: "closed", labelId: "application.closed", fallback: "Clôturées" },
];

const getStatusBadge = (app) => {
  const s = app.status;
  if (s === "accepted") return { labelId: "candidate.applications.status_accepted", color: "hsl(153, 68%, 25%)", bg: "hsla(153, 68%, 25%, 0.1)" };
  if (s === "open") return { labelId: "candidate.applications.status_open", color: "hsl(153, 68%, 25%)", bg: "hsla(153, 68%, 25%, 0.1)" };
  if (s === "closed") return { labelId: "candidate.applications.status_closed", color: "hsl(0, 84%, 60%)", bg: "hsla(0, 84%, 60%, 0.1)" };
  return { labelId: "candidate.applications.status_pending", color: "hsl(38, 92%, 50%)", bg: "hsla(38, 92%, 50%, 0.1)" };
};

// Deep-linkable view mode: ?view=board (alias: table) or ?view=list.
const getInitialViewMode = () => {
  const view = new URLSearchParams(window.location.search).get("view");
  return view === "board" || view === "table" ? "board" : "list";
};

const ApplicationsTab = ({ onNavigate, active = true }) => {
  const intl = useIntl();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"), { noSsr: true });
  const authenticityToken = useSelector((state) => state.authenticity_token);
  const i18nLocale = useSelector((state) => state.i18nLocale);

  moment.locale(i18nLocale === "fr-fr" ? "fr" : (i18nLocale || "fr"));

  // Only the standard reasons: whatever the recruiter typed under "Other" stays internal.
  const visibleDenialReasons = (app) => (
    app.status === "closed" && app.candidate_denial_reasons?.length > 0
      ? getDenialReasons(app.candidate_denial_reasons, i18nLocale, true)
      : ""
  );

  const [viewMode, setViewMode] = useState(getInitialViewMode);
  const [detailApp, setDetailApp] = useState(null);

  // Track the two application views separately: the plain list vs. the Kanban board.
  useEffect(() => {
    trackEvent(
      "candidate_applications",
      viewMode === "board" ? "board_viewed" : "list_viewed"
    );
  }, [viewMode]);

  const [applications, setApplications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [fetchError, setFetchError] = useState(false);
  const [filter, setFilter] = useState("all");
  const [page, setPage] = useState(1);
  const [pageCount, setPageCount] = useState(1);
  const [counts, setCounts] = useState({ all: 0, pending: 0, open: 0, closed: 0 });
  const [tappedStage, setTappedStage] = useState(null);

  const handleStageTap = useCallback((id) => {
    setTappedStage((prev) => (prev === id ? null : id));
  }, []);

  const handleViewModeChange = (key) => {
    setViewMode(key);
    const url = new URL(window.location.href);
    url.searchParams.set("view", key);
    window.history.replaceState(null, "", url.toString());
  };

  // sh cancels an in-flight request when the same URL is requested again, so only the latest
  // fetch may touch state: a cancelled or superseded one must not show the error screen.
  const latestFetchRef = useRef(0);
  const fetchApplications = useCallback((p = 1, status = "all") => {
    const fetchId = ++latestFetchRef.current;
    setLoading(true);
    setFetchError(false);
    sh.get("job_application", {
      authenticity_token: authenticityToken,
      params: { page: p, status },
    })
      .then((res) => {
        if (fetchId !== latestFetchRef.current) return;
        setApplications(res.data?.data || []);
        setPageCount(res.data?.pagy?.pages || 1);
        if (res.data?.counts) setCounts(res.data.counts);
      })
      .catch((err) => {
        if (axios.isCancel(err) || fetchId !== latestFetchRef.current) return;
        setFetchError(true);
      })
      .finally(() => {
        if (fetchId === latestFetchRef.current) setLoading(false);
      });
  }, [authenticityToken]);

  useEffect(() => {
    fetchApplications(page, filter);
  }, [page, filter]);

  // Reset the page in the same update as the filter, so a single fetch runs with page 1
  // instead of one with the stale page followed by a second one.
  const handleFilterChange = (key) => {
    setFilter(key);
    setPage(1);
  };

  // Tabs stay mounted in the dashboard shell, so refetch when this tab is
  // re-activated (e.g. after saving a new application) to avoid stale data.
  const activatedRef = useRef(false);
  useEffect(() => {
    if (!active) return;
    if (!activatedRef.current) { activatedRef.current = true; return; }
    fetchApplications(page, filter);
  }, [active]);

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: "16px" }}>

      {/* View mode toggle — the kanban board is unusable on phones, list only */}
      <Box sx={{ display: { xs: "none", sm: "flex" }, alignItems: "center", justifyContent: "flex-end", gap: "4px" }}>
        {[
          { key: "list", Icon: List, label: intl.formatMessage({ id: "candidate.applications.view_list" }) },
          { key: "board", Icon: Kanban, label: intl.formatMessage({ id: "candidate.applications.view_board" }) },
        ].map(({ key, Icon, label }) => {
          const active = viewMode === key;
          return (
            <Box
              key={key}
              component="button"
              onClick={() => handleViewModeChange(key)}
              sx={{
                display: "flex", alignItems: "center", gap: "5px",
                px: "10px", py: "5px", borderRadius: "8px",
                fontSize: "11px", border: "none", cursor: "pointer",
                fontFamily: "Segoe UI Bold, sans-serif, system-ui",
                bgcolor: active ? "hsl(178, 93%, 10%)" : "transparent",
                color: active ? "hsl(0, 0%, 100%)" : "hsl(178, 10%, 55%)",
                transition: "all 0.15s",
                "&:hover": {
                  bgcolor: active ? "hsl(178, 93%, 10%)" : "hsla(160, 10%, 96%, 0.8)",
                },
              }}
            >
              <Icon size={13} />
              {label}
            </Box>
          );
        })}
      </Box>

      {viewMode === "board" && !isMobile && <CandidateKanbanBoard active={active} />}

      {(viewMode === "list" || isMobile) && (
        <>

          {/* Filter tabs */}
          <Box sx={{ display: "flex", gap: "6px" }}>
            {filterConfig.map((f) => {
              const active = filter === f.key;
              return (
                <Box
                  key={f.key}
                  component="button"
                  onClick={() => handleFilterChange(f.key)}
                  sx={{
                    display: "flex", alignItems: "center", gap: "4px",
                    px: "12px", py: "6px", borderRadius: "9999px",
                    fontSize: "11px", border: "none", cursor: "pointer",
                    fontFamily: "Segoe UI Bold, sans-serif, system-ui",
                    bgcolor: active ? "hsl(178, 93%, 10%)" : "hsla(160, 10%, 96%, 0.5)",
                    color: active ? "hsl(0, 0%, 100%)" : "hsl(178, 10%, 45%)",
                    transition: "all 0.15s",
                    "&:hover": {
                      bgcolor: active ? "hsl(178, 93%, 10%)" : "hsla(160, 10%, 96%, 0.8)",
                      color: active ? "hsl(0, 0%, 100%)" : "hsl(178, 93%, 10%)",
                    },
                  }}
                >
                  {intl.formatMessage({ id: f.labelId, defaultMessage: f.fallback })}
                  <Box component="span" sx={{
                    fontSize: "11px", opacity: active ? 0.7 : 0.5,
                    fontVariantNumeric: "tabular-nums",
                  }}>
                    {counts[f.key]}
                  </Box>
                </Box>
              );
            })}
          </Box>

          {/* Loading: the branded loader, centred where the list will appear */}
          {loading && <SquarehubLoader size={56} centered minHeight="50vh" />}

          {/* Fetch error: explicit state with retry */}
          {!loading && fetchError && (
            <EmptyState
              icon={AlertCircle}
              py="64px"
              title={intl.formatMessage({ id: "candidate.dashboard.load_error_title", defaultMessage: "Impossible de charger les données" })}
              subtitle={intl.formatMessage({ id: "candidate.dashboard.load_error_hint", defaultMessage: "Vérifiez votre connexion internet." })}
              ctaLabel={intl.formatMessage({ id: "candidate.dashboard.retry", defaultMessage: "Réessayer" })}
              onCta={() => fetchApplications(page, filter)}
            />
          )}

          {/* Application cards */}
          {!loading && (
            <Box sx={{ display: "flex", flexDirection: "column", gap: "12px" }}>
              <AnimatePresence mode="popLayout">
                {applications.map((app, i) => {
                  const badge = getStatusBadge(app);
                  const jobOffer = app.job_offer;
                  const company = app.company || jobOffer?.company;
                  const isClosed = app.status === "closed" || app.status === "rejected";
                  const pipeline = getAppPipeline(app);

                  return (
                    <motion.div
                      key={app.id}
                      initial={{ opacity: 0, y: 8 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0, y: -8 }}
                      transition={{ delay: i * 0.04, duration: 0.3 }}
                    >
                      <Box
                        onClick={() => setDetailApp(app)}
                        sx={{
                          bgcolor: "hsl(0, 0%, 100%)",
                          borderRadius: "16px",
                          border: "1px solid",
                          borderColor: isClosed ? "hsla(160, 10%, 90%, 0.4)" : "hsla(160, 10%, 90%, 0.6)",
                          p: "20px",
                          transition: "all 0.3s",
                          opacity: isClosed ? 0.6 : 1,
                          cursor: "pointer",
                          "&:hover": {
                            borderColor: "hsla(145, 63%, 42%, 0.3)",
                            boxShadow: "0 5px 5px -3px hsla(145, 63%, 42%, 0.05), 0 4px 6px -4px hsla(145, 63%, 42%, 0.05)",
                          },
                        }}>
                        {/* Header row: logo + info */}
                        <Box sx={{ display: "flex", alignItems: "flex-start", gap: "16px" }}>
                          {/* Company logo */}
                          <Box sx={{
                            width: 44, height: 44, borderRadius: "12px",
                            bgcolor: "hsla(153, 68%, 25%, 0.1)",
                            display: "flex", alignItems: "center", justifyContent: "center",
                            overflow: "hidden", flexShrink: 0,
                            border: "1px solid hsla(160, 10%, 90%, 0.5)",
                          }}>
                            {company?.logo_url ? (
                              <Box
                                component="img"
                                src={company.logo_url}
                                alt={company.name}
                                loading="lazy"
                                sx={{ width: 44, height: 44, objectFit: "contain" }}
                              />
                            ) : (
                              <Typography sx={{
                                fontSize: "16px",
                                fontFamily: "Segoe UI Bold, sans-serif, system-ui",
                                color: "hsl(153, 68%, 25%)", userSelect: "none",
                              }}>
                                {company?.name?.charAt(0)?.toUpperCase()}
                              </Typography>
                            )}
                          </Box>

                          {/* Title + badge + meta */}
                          <Box sx={{ flex: 1, minWidth: 0 }}>
                            <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: "12px" }}>
                              <Typography sx={{
                                fontFamily: "Segoe UI Bold, sans-serif, system-ui",
                                fontSize: "14px", color: "hsl(178, 93%, 10%)",
                                whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis",
                              }}>
                                {jobOffer ? jobOffer.job_title : intl.formatMessage({ id: "spontaneous", defaultMessage: "Candidature spontanée" })}
                              </Typography>
                              <Box sx={{
                                fontSize: "10px", fontFamily: "Segoe UI Semibold, sans-serif, system-ui",
                                px: "10px", py: "2px", borderRadius: "9999px", flexShrink: 0,
                                bgcolor: badge.bg, color: badge.color,
                                border: `1px solid ${badge.color}20`,
                              }}>
                                {intl.formatMessage({ id: badge.labelId })}
                              </Box>
                            </Box>

                            <Box sx={{ display: "flex", alignItems: "center", gap: "8px", mt: "2px" }}>
                              <Typography sx={{
                                fontSize: "12px", color: "hsl(178, 10%, 45%)",
                                fontFamily: "Segoe UI Regular, sans-serif, system-ui",
                              }}>
                                {company?.name}
                              </Typography>
                              <Box component="span" sx={{ fontSize: "10px", color: "hsla(178, 10%, 45%, 0.5)" }}>·</Box>
                              <Typography sx={{
                                fontSize: "10px", color: "hsl(178, 10%, 45%)",
                                fontFamily: "Segoe UI Regular, sans-serif, system-ui",
                              }}>
                                {intl.formatMessage({ id: "candidate.applications.applied_ago" }, { time: moment(app.created_at).fromNow() })}
                              </Typography>
                            </Box>
                          </Box>
                        </Box>

                        {/* Pipeline segment stepper */}
                        {pipeline.total > 0 && (
                          <Box sx={{ display: "flex", width: "100%", gap: "4px", mt: "16px", overflow: "hidden" }}>
                            {pipeline.stages.map((name, idx) => {
                              const stageId = `${app.id}-${idx}`;
                              const isTapped = tappedStage === stageId;
                              const current = pipeline.current;

                              const getSegmentColor = () => {
                                if (isClosed && idx === current) return "hsla(0, 84%, 60%, 0.6)";
                                if (isClosed && idx < current) return "hsla(0, 84%, 60%, 0.3)";
                                if (idx < current) return "hsla(153, 68%, 25%, 0.6)";
                                if (idx === current) return "hsl(153, 68%, 25%)";
                                return "hsl(160, 10%, 90%)";
                              };

                              const getLabelColor = () => {
                                if (idx === current) return "hsl(153, 68%, 25%)";
                                if (idx < current) return "hsl(178, 10%, 45%)";
                                return "hsla(178, 10%, 45%, 0.4)";
                              };

                              return (
                                <Tooltip key={idx} title={
                                  <Box component="span" sx={{ fontSize: "11px", fontFamily: "Segoe UI Semibold, sans-serif, system-ui" }}>
                                    {name}
                                    {idx === current && !isClosed && <Box component="span" sx={{ ml: "4px", color: "hsl(153, 68%, 25%)" }}>●</Box>}
                                    {idx === current && isClosed && <Box component="span" sx={{ ml: "4px", color: "hsl(0, 84%, 60%)" }}>●</Box>}
                                  </Box>
                                } placement="top" arrow>
                                  <Box
                                    onClick={(e) => { e.stopPropagation(); handleStageTap(stageId); }}
                                    sx={{
                                      display: "flex", flexDirection: "column", gap: "6px",
                                      flex: 1, minWidth: 0, cursor: "default",
                                      "&:hover .seg-bar": { transform: "scaleY(1.4)" },
                                    }}
                                  >
                                    <Typography sx={{
                                      fontSize: "11px",
                                      fontFamily: "Segoe UI Semibold, sans-serif, system-ui",
                                      whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis",
                                      color: getLabelColor(),
                                      display: { xs: isTapped ? "block" : "none", sm: "block" },
                                    }}>
                                      {name}
                                    </Typography>
                                    <Box
                                      className="seg-bar"
                                      sx={{
                                        height: "6px", width: "100%",
                                        borderRadius: "2px",
                                        bgcolor: getSegmentColor(),
                                        transition: "all 0.2s",
                                      }}
                                    />
                                  </Box>
                                </Tooltip>
                              );
                            })}
                          </Box>
                        )}

                        {/* Denial reasons */}
                        {visibleDenialReasons(app) && (
                          <Box sx={{ mt: "12px", pt: "12px", borderTop: "1px solid hsla(160, 10%, 90%, 0.4)" }}>
                            <Typography sx={{
                              fontSize: "11px", color: "hsl(0, 84%, 60%)", whiteSpace: "pre-line",
                              fontFamily: "Segoe UI Regular, sans-serif, system-ui",
                            }}>
                              {visibleDenialReasons(app)}
                            </Typography>
                          </Box>
                        )}
                      </Box>
                    </motion.div>
                  );
                })}
              </AnimatePresence>
            </Box>
          )}

          {/* Empty state */}
          {!loading && !fetchError && applications.length === 0 && (
            <EmptyState
              icon={Send}
              py="64px"
              title={intl.formatMessage({ id: "candidate.applications.no_applications" })}
              subtitle={filter === "all"
                ? intl.formatMessage({ id: "candidate.applications.no_applications_hint", defaultMessage: "Postulez à une offre pour la suivre ici." })
                : intl.formatMessage({ id: "candidate.applications.adjust_filters" })}
              ctaLabel={filter === "all"
                ? intl.formatMessage({ id: "candidate.applications.browse_offers" })
                : undefined}
              onCta={filter === "all" && onNavigate ? () => onNavigate("jobs") : undefined}
            />
          )}

          {/* Pagination */}
          {!loading && pageCount > 1 && (
            <Box sx={{ display: "flex", justifyContent: "center", pt: "8px" }}>
              <Pagination
                count={pageCount}
                page={page}
                onChange={(_, v) => setPage(v)}
                shape="rounded"
                sx={{
                  "& .MuiPaginationItem-root": {
                    fontFamily: "Segoe UI Bold, sans-serif, system-ui",
                    fontSize: "12px", color: "hsl(178, 10%, 45%)",
                    "&.Mui-selected": {
                      bgcolor: "hsl(178, 93%, 10%)", color: "hsl(0, 0%, 100%)",
                      "&:hover": { bgcolor: "hsl(178, 93%, 10%)" },
                    },
                  },
                }}
              />
            </Box>
          )}

        </>
      )}

      {/* Read-only application detail */}
      <Dialog
        open={Boolean(detailApp)}
        onClose={() => setDetailApp(null)}
        fullScreen={isMobile}
        maxWidth="sm"
        fullWidth
        PaperProps={{ sx: { borderRadius: isMobile ? 0 : "16px" } }}
      >
        {detailApp && (() => {
          const app = detailApp;
          const badge = getStatusBadge(app);
          const company = app.company || app.job_offer?.company || {};
          const pipeline = getAppPipeline(app);
          const isClosed = app.status === "closed" || app.status === "rejected";
          return (
            <Box sx={{ p: "20px", display: "flex", flexDirection: "column", gap: "20px" }}>
              {/* Header */}
              <Box sx={{ display: "flex", alignItems: "flex-start", gap: "12px" }}>
                <Box sx={{
                  width: 44, height: 44, borderRadius: "12px", flexShrink: 0,
                  bgcolor: "hsla(153, 68%, 25%, 0.1)", overflow: "hidden",
                  display: "flex", alignItems: "center", justifyContent: "center",
                  border: "1px solid hsla(160, 10%, 90%, 0.5)",
                }}>
                  {company?.logo_url ? (
                    <Box component="img" src={company.logo_url} alt={company.name} sx={{ width: 44, height: 44, objectFit: "contain" }} />
                  ) : (
                    <Typography sx={{ fontSize: "16px", fontFamily: "Segoe UI Bold, sans-serif, system-ui", color: "hsl(153, 68%, 25%)" }}>
                      {company?.name?.charAt(0)?.toUpperCase()}
                    </Typography>
                  )}
                </Box>
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Typography sx={{ fontFamily: "Segoe UI Bold, sans-serif, system-ui", fontSize: "16px", color: "hsl(178, 93%, 10%)" }}>
                    {app.job_offer ? app.job_offer.job_title : intl.formatMessage({ id: "spontaneous", defaultMessage: "Candidature spontanée" })}
                  </Typography>
                  <Typography sx={{ fontSize: "13px", color: "hsl(178, 10%, 45%)", fontFamily: "Segoe UI Regular, sans-serif, system-ui" }}>
                    {company?.name}
                  </Typography>
                  <Box sx={{ display: "flex", alignItems: "center", gap: "8px", mt: "6px", flexWrap: "wrap" }}>
                    <Box sx={{
                      fontSize: "11px", fontFamily: "Segoe UI Semibold, sans-serif, system-ui",
                      px: "10px", py: "3px", borderRadius: "9999px",
                      bgcolor: badge.bg, color: badge.color,
                    }}>
                      {intl.formatMessage({ id: badge.labelId })}
                    </Box>
                    <Typography sx={{ fontSize: "12px", color: "hsl(178, 10%, 45%)", fontFamily: "Segoe UI Regular, sans-serif, system-ui" }}>
                      {intl.formatMessage({ id: "candidate.applications.applied_ago" }, { time: moment(app.created_at).fromNow() })}
                    </Typography>
                  </Box>
                </Box>
                <Box
                  component="button"
                  onClick={() => setDetailApp(null)}
                  aria-label={intl.formatMessage({ id: "candidate.applications.close_detail", defaultMessage: "Fermer" })}
                  sx={{
                    width: "44px", height: "44px", mt: "-8px", mr: "-8px", flexShrink: 0,
                    display: "flex", alignItems: "center", justifyContent: "center",
                    border: "none", bgcolor: "transparent", cursor: "pointer",
                    borderRadius: "12px", color: "hsl(178, 10%, 45%)",
                    "&:hover": { color: "hsl(178, 93%, 10%)", bgcolor: "hsla(160, 10%, 96%, 0.8)" },
                  }}
                >
                  <X size={18} />
                </Box>
              </Box>

              {/* Pipeline timeline */}
              {pipeline.total > 0 && (
                <Box>
                  <Typography sx={{ fontSize: "12px", fontFamily: "Segoe UI Bold, sans-serif, system-ui", color: "hsl(178, 93%, 10%)", mb: "10px" }}>
                    {intl.formatMessage({ id: "candidate.applications.detail_timeline", defaultMessage: "Progression" })}
                  </Typography>
                  <Box sx={{ display: "flex", flexDirection: "column", gap: "8px" }}>
                    {pipeline.stages.map((name, idx) => {
                      const isCurrent = idx === pipeline.current;
                      const isPast = idx < pipeline.current;
                      const dotColor = isClosed && isCurrent
                        ? "hsl(0, 84%, 60%)"
                        : isCurrent || isPast ? "hsl(153, 68%, 25%)" : "hsl(160, 10%, 85%)";
                      return (
                        <Box key={idx} sx={{ display: "flex", alignItems: "center", gap: "10px" }}>
                          {isPast ? (
                            <CheckCircle size={16} color={dotColor} style={{ flexShrink: 0 }} />
                          ) : (
                            <Box sx={{
                              width: "10px", height: "10px", mx: "3px", borderRadius: "9999px", flexShrink: 0,
                              bgcolor: dotColor,
                              outline: isCurrent ? `3px solid ${isClosed ? "hsla(0, 84%, 60%, 0.2)" : "hsla(153, 68%, 25%, 0.2)"}` : "none",
                            }} />
                          )}
                          <Typography sx={{
                            fontSize: "13px",
                            fontFamily: isCurrent ? "Segoe UI Bold, sans-serif, system-ui" : "Segoe UI Regular, sans-serif, system-ui",
                            color: isCurrent ? "hsl(178, 93%, 10%)" : "hsl(178, 10%, 45%)",
                          }}>
                            {name}
                          </Typography>
                        </Box>
                      );
                    })}
                  </Box>
                </Box>
              )}

              {/* Denial reasons */}
              {visibleDenialReasons(app) && (
                <Typography sx={{ fontSize: "12px", color: "hsl(0, 84%, 60%)", whiteSpace: "pre-line", fontFamily: "Segoe UI Regular, sans-serif, system-ui" }}>
                  {visibleDenialReasons(app)}
                </Typography>
              )}

              {/* Documents sent */}
              {app.resume_url && (
                <Box>
                  <Typography sx={{ fontSize: "12px", fontFamily: "Segoe UI Bold, sans-serif, system-ui", color: "hsl(178, 93%, 10%)", mb: "8px" }}>
                    {intl.formatMessage({ id: "candidate.applications.detail_cv", defaultMessage: "CV envoyé" })}
                  </Typography>
                  <Box
                    component="a"
                    href={app.resume_url}
                    target="_blank"
                    rel="noopener noreferrer"
                    sx={{
                      display: "flex", alignItems: "center", gap: "10px",
                      p: "12px", borderRadius: "12px", textDecoration: "none",
                      bgcolor: "hsla(160, 10%, 96%, 0.3)", border: "1px solid hsla(160, 10%, 90%, 0.4)",
                      "&:hover": { borderColor: "hsla(153, 68%, 25%, 0.3)" },
                    }}
                  >
                    <FileText size={16} color="hsl(153, 68%, 25%)" style={{ flexShrink: 0 }} />
                    <Typography sx={{ fontSize: "13px", color: "hsl(178, 93%, 10%)", fontFamily: "Segoe UI Semibold, sans-serif, system-ui" }}>
                      {intl.formatMessage({ id: "candidate.applications.detail_cv", defaultMessage: "CV envoyé" })}
                    </Typography>
                  </Box>
                </Box>
              )}

              {/* Cover letter */}
              {app.cover_letter && (
                <Box>
                  <Typography sx={{ fontSize: "12px", fontFamily: "Segoe UI Bold, sans-serif, system-ui", color: "hsl(178, 93%, 10%)", mb: "8px" }}>
                    {intl.formatMessage({ id: "candidate.applications.detail_cover_letter", defaultMessage: "Lettre de motivation" })}
                  </Typography>
                  <Typography sx={{
                    fontSize: "13px", color: "hsl(178, 10%, 45%)", lineHeight: 1.6,
                    fontFamily: "Segoe UI Regular, sans-serif, system-ui",
                    p: "12px", borderRadius: "12px",
                    bgcolor: "hsla(160, 10%, 96%, 0.3)", border: "1px solid hsla(160, 10%, 90%, 0.4)",
                    whiteSpace: "pre-wrap",
                  }}>
                    {app.cover_letter}
                  </Typography>
                </Box>
              )}

              {/* View offer */}
              {app.job_offer && onNavigate && (
                <Box
                  component="button"
                  onClick={() => { setDetailApp(null); onNavigate("jobs", app.job_offer); }}
                  sx={{
                    display: "flex", alignItems: "center", justifyContent: "center", gap: "8px",
                    py: "12px", borderRadius: "12px", cursor: "pointer",
                    border: "1px solid hsl(160, 10%, 90%)", bgcolor: "hsl(0, 0%, 100%)",
                    color: "hsl(178, 93%, 10%)", fontSize: "13px",
                    fontFamily: "Segoe UI Bold, sans-serif, system-ui",
                    "&:hover": { bgcolor: "hsla(160, 10%, 96%, 0.5)" },
                    transition: "background 0.15s",
                  }}
                >
                  {intl.formatMessage({ id: "candidate.applications.view_offer", defaultMessage: "Voir l'offre" })}
                  <ArrowRight size={14} />
                </Box>
              )}
            </Box>
          );
        })()}
      </Dialog>
    </Box>
  );
};

export default ApplicationsTab;
